import { useCallback, useState } from "react";
import { Navigate } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { PageLayout } from "@/components/layout/PageLayout";
import { ROUTE_HOME } from "@/constants/routes";

import "./LoginPage.css";

/**
 * LoginPage — Microsoft-only operator sign-in card for the admin surface.
 *
 * Structural port of client_frontend's AuthPage.tsx. The client AuthPage
 * supports email+password + signup + OAuth providers; the admin surface
 * is SSO-only, so we strip every input and keep just the Microsoft button.
 *
 * The page renders inside <WebsiteLayout /> (Header + main + Footer) via
 * the route declared in App.tsx.
 */
export function LoginPage(): JSX.Element {
  const { firebaseUser, loading, signInMicrosoft } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleMicrosoftLogin = useCallback(async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      // signInWithRedirect navigates away — page will redirect to Microsoft.
      // On return, onAuthStateChanged flips firebaseUser and this page
      // redirects to <ROUTE_HOME /> on next render.
      await signInMicrosoft();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign-in failed";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }, [signInMicrosoft]);

  // Spinner while Firebase resolves auth state (e.g. post-redirect return).
  if (loading) {
    return (
      <section className="auth-page">
        <div
          className="spinner-container"
          role="status"
          aria-live="polite"
        >
          <div className="spinner spinner--lg" aria-hidden="true" />
          <span className="sr-only">Signing you in…</span>
        </div>
      </section>
    );
  }

  // Redirect authenticated operators away from /login.
  if (firebaseUser) return <Navigate to={ROUTE_HOME} replace />;

  return (
    <PageLayout
      title="Sign In — Soft9Tech Admin"
      description="Sign in with your Soft9Tech Microsoft account to access the admin surface."
      className="auth-page"
    >
      <div className="auth-card card">
        <div className="auth-card__header">
          <h1 className="auth-card__title">Soft9Tech Admin</h1>
          <p className="auth-card__subtitle">
            Sign in with your Soft9Tech Microsoft account.
          </p>
        </div>

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        <div className="auth-social">
          <button
            type="button"
            className="btn btn--primary btn--full auth-social__btn btn-microsoft"
            onClick={handleMicrosoftLogin}
            disabled={submitting}
          >
            {submitting ? (
              <span className="spinner" aria-hidden="true" />
            ) : (
              <MicrosoftIcon />
            )}
            <span>Sign in with Microsoft</span>
          </button>
        </div>
      </div>
    </PageLayout>
  );
}

function MicrosoftIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 21 21"
      fill="none"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
